import React, { useState, useEffect } from 'react';
import Icon3D from './Icon3D';

const APP_STORE_URL = 'https://apps.apple.com/in/app/flatloop-flat-room-finder/id6766100303';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.flatloop.app&hl=en_IN';
const DISMISS_KEY = 'appBannerDismissedAt';

/* ── Detect platform ─────────────────────────────────── */
const getDeviceType = () => {
    const ua = navigator.userAgent || '';
    if (/android/i.test(ua)) return 'android';
    if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
    return 'other';
};

const getStoreUrl = () => {
    const device = getDeviceType();
    if (device === 'ios') return APP_STORE_URL;
    return PLAY_STORE_URL; // Default to Play Store for android & desktop
};

/* ── Smart App Banner Component ──────────────────────── */
const AppDownloadBanner = () => {
    const [visible, setVisible] = useState(false);
    const [showFullModal, setShowFullModal] = useState(false);

    useEffect(() => {
        // Only show on mobile / tablet
        if (window.innerWidth > 768) return;

        // Check if user dismissed recently (within 3 days)
        const dismissedAt = localStorage.getItem(DISMISS_KEY);
        if (dismissedAt) {
            const threeDays = 3 * 24 * 60 * 60 * 1000;
            if (Date.now() - parseInt(dismissedAt) < threeDays) return;
        }

        // Show small banner after 2s delay
        const timer = setTimeout(() => setVisible(true), 2000);

        // Show full modal after 30 seconds of browsing
        const modalTimer = setTimeout(() => setShowFullModal(true), 30000);

        return () => {
            clearTimeout(timer);
            clearTimeout(modalTimer);
        };
    }, []);

    const dismiss = () => {
        setVisible(false);
        setShowFullModal(false);
        localStorage.setItem(DISMISS_KEY, Date.now().toString());
    };

    const openStore = () => {
        window.open(getStoreUrl(), '_blank');
    };

    const device = getDeviceType();

    if (!visible && !showFullModal) return null;

    return (
        <>
            {/* ── Responsive Styles ──────────────────── */}
            <style>{`
                @keyframes bannerSlideUp {
                    from { transform: translateY(100%); opacity: 0; }
                    to   { transform: translateY(0);    opacity: 1; }
                }
                @keyframes modalFadeIn {
                    from { opacity: 0; }
                    to   { opacity: 1; }
                }
                @keyframes modalCardSlide {
                    from { transform: translateY(40px); opacity: 0; }
                    to   { transform: translateY(0);    opacity: 1; }
                }
                .app-banner-bottom {
                    position: fixed; bottom: 0; left: 0; right: 0; z-index: 9998;
                    animation: bannerSlideUp 0.4s ease-out;
                    padding: 0 12px 12px;
                }
                .app-banner-card {
                    max-width: 480px; margin: 0 auto;
                    background: linear-gradient(135deg, #1E40AF 0%, #2563EB 50%, #3B82F6 100%);
                    border-radius: 20px; padding: 16px 18px;
                    display: flex; align-items: center; gap: 14px;
                    box-shadow: 0 -4px 32px rgba(34,106,70,0.35), 0 8px 24px rgba(0,0,0,0.15);
                }
                .app-modal-overlay {
                    position: fixed; inset: 0; z-index: 10000;
                    background: rgba(0,0,0,0.55); backdrop-filter: blur(6px);
                    display: flex; align-items: flex-end; justify-content: center;
                    animation: modalFadeIn 0.3s ease;
                    padding: 16px;
                }
                .app-modal-sheet {
                    width: 100%; max-width: 420px;
                    background: white; border-radius: 28px 28px 20px 20px;
                    overflow: hidden;
                    animation: modalCardSlide 0.35s ease-out;
                    margin-bottom: env(safe-area-inset-bottom, 0);
                }
                @media (min-width: 769px) {
                    .app-banner-bottom { display: none; }
                    .app-modal-overlay { display: none; }
                }
            `}</style>

            {/* ═══════════ STICKY BOTTOM BANNER ═══════════ */}
            {visible && !showFullModal && (
                <div className="app-banner-bottom">
                    <div className="app-banner-card">
                        {/* App icon */}
                        <div style={{
                            width: '48px', height: '48px', borderRadius: '14px', flexShrink: 0,
                            background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            border: '1px solid rgba(255,255,255,0.3)',
                        }}>
                            <img src="/flatloop.png" alt="FlatLoop" style={{ height: '28px', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
                        </div>

                        {/* Text */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ color: 'white', fontWeight: '800', fontSize: '14px', letterSpacing: '-0.3px' }}>
                                Get FlatLoop App
                            </div>
                            <div style={{ color: '#D9E6DC', fontSize: '12px', fontWeight: '500', marginTop: '2px' }}>
                                Faster search · Chat · Notifications
                            </div>
                        </div>

                        {/* Open button */}
                        <button
                            onClick={openStore}
                            style={{
                                background: 'white', color: '#1E40AF', border: 'none',
                                padding: '10px 20px', borderRadius: '14px', fontWeight: '800',
                                fontSize: '13px', cursor: 'pointer', flexShrink: 0,
                                boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                            }}
                        >
                            OPEN
                        </button>

                        {/* Close button */}
                        <button
                            onClick={dismiss}
                            aria-label="Close app banner"
                            style={{
                                background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)',
                                fontSize: '18px', cursor: 'pointer', padding: '4px',
                                lineHeight: 1, flexShrink: 0,
                            }}
                        >
                            ✕
                        </button>
                    </div>
                </div>
            )}

            {/* ═══════════ FULL-SCREEN MODAL (shows after 30s) ═══════════ */}
            {showFullModal && (
                <div className="app-modal-overlay" onClick={dismiss}>
                    <div className="app-modal-sheet" onClick={e => e.stopPropagation()}>
                        {/* Header gradient */}
                        <div style={{
                            background: 'linear-gradient(155deg, #1E40AF 0%, #2563EB 55%, #3B82F6 100%)',
                            padding: '36px 24px 32px', textAlign: 'center', position: 'relative',
                        }}>
                            {/* Decorative orbs */}
                            <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '120px', height: '120px', background: 'rgba(255,255,255,0.06)', borderRadius: '50%' }} />

                            {/* Close */}
                            <button
                                onClick={dismiss}
                                style={{
                                    position: 'absolute', top: '12px', right: '12px',
                                    background: 'rgba(255,255,255,0.2)', border: 'none',
                                    width: '32px', height: '32px', borderRadius: '50%',
                                    color: 'white', fontSize: '16px', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                            >
                                ✕
                            </button>

                            {/* App icon */}
                            <div style={{
                                width: '72px', height: '72px', borderRadius: '20px',
                                background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)',
                                border: '1px solid rgba(255,255,255,0.3)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                margin: '0 auto 16px',
                            }}>
                                <img src="/flatloop.png" alt="FlatLoop" style={{ height: '40px', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
                            </div>

                            <h2 style={{ color: 'white', fontSize: '22px', fontWeight: '800', margin: '0 0 6px', letterSpacing: '-0.5px' }}>
                                Get the Full Experience
                            </h2>
                            <p style={{ color: '#D9E6DC', fontSize: '14px', margin: 0, lineHeight: 1.5 }}>
                                The FlatLoop app is faster, smoother & has exclusive features
                            </p>
                        </div>

                        {/* Features list */}
                        <div style={{ padding: '24px 24px 12px' }}>
                            {[
                                { icon: 'bell', title: 'Instant Notifications', desc: 'Get notified when new flats match your criteria' },
                                { icon: 'chat', title: 'Real-time Chat', desc: 'Chat directly with owners — no waiting' },
                                { icon: 'all', title: 'Filtered Search', desc: 'Filter by budget, locality, gender, furnishing and occupancy' },
                                { icon: 'verified', title: 'Phone-Verified Users', desc: 'Every account is verified by phone OTP before it can post or message' },
                            ].map((f, i) => (
                                <div key={i} style={{
                                    display: 'flex', gap: '14px', alignItems: 'center',
                                    padding: '12px 0',
                                    borderBottom: i < 3 ? '1px solid #E5E9E3' : 'none',
                                }}>
                                    <div style={{
                                        width: '42px', height: '42px', borderRadius: '14px',
                                        background: '#EFF6FF', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0,
                                    }}>
                                        <Icon3D name={f.icon} size={24} />
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: '700', fontSize: '14px', color: '#111827' }}>{f.title}</div>
                                        <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{f.desc}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Action buttons */}
                        <div style={{ padding: '12px 24px 24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <button
                                onClick={openStore}
                                style={{
                                    width: '100%', padding: '16px', border: 'none',
                                    borderRadius: '16px', fontWeight: '800', fontSize: '15px',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', gap: '8px',
                                    background: 'linear-gradient(135deg, #1E40AF, #3B82F6)',
                                    color: 'white',
                                    boxShadow: '0 8px 24px rgba(34,106,70,0.3)',
                                }}
                            >
                                {device === 'ios' ? '🍎' : '▶️'} Download {device === 'ios' ? 'on App Store' : 'on Play Store'}
                            </button>
                            <button
                                onClick={dismiss}
                                style={{
                                    width: '100%', padding: '14px', border: 'none',
                                    borderRadius: '14px', fontWeight: '600', fontSize: '14px',
                                    cursor: 'pointer', background: '#E5E9E3', color: '#6B7280',
                                }}
                            >
                                Continue on Website
                            </button>
                        </div>

                        {/* Rating bar */}
                        <div style={{
                            background: '#F3F4F6', padding: '14px 24px',
                            borderTop: '1px solid #E5E9E3',
                            display: 'flex', justifyContent: 'center', gap: '24px',
                        }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontWeight: '800', fontSize: '16px', color: '#111827' }}>10</div>
                                <div style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: '600' }}>Cities</div>
                            </div>
                            <div style={{ width: '1px', background: '#DDE3DC' }} />
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontWeight: '800', fontSize: '16px', color: '#111827' }}>OTP</div>
                                <div style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: '600' }}>Verified</div>
                            </div>
                            <div style={{ width: '1px', background: '#DDE3DC' }} />
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontWeight: '800', fontSize: '16px', color: '#111827' }}>₹0</div>
                                <div style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: '600' }}>Brokerage</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AppDownloadBanner;
